import { Trans } from "turboui";
import { useTranslation } from "react-i18next";
import { useTaskList } from "@/models/richContent/taskListLifecycle";
import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";
import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as PageOptions from "@/components/PaperContainer/PageOptions";
import { RetroReactions } from "./RetroReactions";
import * as React from "react";

import { ProjectPageNavigation } from "@/components/ProjectPageNavigation";
import { Comments } from "./Comments";
import { useCurrentSubscriptionsQueryAdapter } from "@/models/subscriptions/useCurrentSubscriptionsQueryAdapter";
import {
  AvatarWithName,
  IconEdit,
  IconSquareCheckFilled,
  StatusBadge,
  CurrentSubscriptions,
  parseContent,
  RichContent,
  FormattedTime,
  Spacer,
} from "turboui";
import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";
import { useReadNotificationsOnLoad } from "@/models/notifications/notificationLifecycle";
import { invalidateProjectInteractionQueries } from "@/models/projects/projectInteractionQueries";
import { assertPresent } from "@/utils/assertions";
import { useLoadedData, useRefresh } from "./loader";
import { AckCTA } from "./AckCTA";

import { usePaths } from "@/routes/paths";

export function Page() {
  const { t } = useTranslation();
  const { retrospective } = useLoadedData();

  useReadNotificationsOnLoad(retrospective.notifications, (client) =>
    invalidateProjectInteractionQueries(
      client,
      {
        projectId: retrospective.project.id,
        spaceId: retrospective.project.space?.id,
        resourceId: retrospective.id,
        resourceType: "project_retrospective",
      },
      "none",
    ),
  );

  return (
    <Pages.Page title={[t("Retrospective"), retrospective.project!.name!]}>
      <Paper.Root size="medium">
        <ProjectPageNavigation project={retrospective.project!} />

        <Paper.Body minHeight="none">
          <Options />

          <Header />
          <Status />
          <RetrospectiveContent />

          <AckCTA />

          <Spacer size={2} />
          <RetroReactions />

          <Spacer size={4} />
          <Comments />

          <Subscriptions />
        </Paper.Body>
      </Paper.Root>
    </Pages.Page>
  );
}

function Options() {
  const { t } = useTranslation();
  const paths = usePaths();
  const { retrospective } = useLoadedData();

  return (
    <PageOptions.Root testId="project-options-button">
      {retrospective.permissions?.canEdit && (
        <PageOptions.Link
          icon={IconEdit}
          title={t("Edit retrospective")}
          to={paths.projectRetrospectiveEditPath(retrospective.project!.id!)}
          testId="edit-retrospective"
        />
      )}
    </PageOptions.Root>
  );
}

function Header() {
  const { t } = useTranslation();
  const { retrospective } = useLoadedData();
  const formattedTimePreferences = useFormattedTimePreferences();

  assertPresent(retrospective.closedAt, "closedAt must be present in retrospective");

  return (
    <>
      <div className="text-center text-content-accent text-3xl font-extrabold">{t("Project Retrospective")}</div>

      <div className="flex items-center gap-1.5 font-medium justify-center mt-2">
        {retrospective.author && <AvatarWithName person={retrospective.author!} size={20} />}
        {retrospective.author && <span>&middot;</span>}
        <FormattedTime {...formattedTimePreferences} time={retrospective.closedAt} format="long-date" />
        <span>&middot;</span>
        <Acknowledgement />
      </div>
    </>
  );
}

function Acknowledgement() {
  const { t } = useTranslation();
  const { retrospective } = useLoadedData();

  if (retrospective.acknowledgedAt) {
    return (
      <span className="flex items-center gap-1">
        <Trans
          i18nKey="<icon/>Acknowledged by {{fullName}}"
          values={{ fullName: retrospective.acknowledgedBy?.fullName }}
          components={{ icon: <IconSquareCheckFilled size={16} className="text-accent-1" /> }}
        />
      </span>
    );
  }

  return <span className="flex items-center gap-1">{t("Not yet acknowledged")}</span>;
}

function Status() {
  const { retrospective } = useLoadedData();

  assertPresent(retrospective.project, "project must be present in retrospective");

  return (
    <div className="mt-4">
      <StatusBadge status={retrospective.project.successStatus} />
    </div>
  );
}

function RetrospectiveContent() {
  const { retrospective } = useLoadedData();

  const content = React.useMemo(() => parseContent(retrospective.content), [retrospective.content]);
  const { mentionedPersonLookup } = useRichEditorHandlers();
  const taskList = useTaskList({
    resourceType: "project_retrospective",
    resourceId: retrospective.id,
    field: "content",
    canEdit: retrospective.permissions?.canEdit ?? false,
  });

  return (
    <div className="my-8">
      <RichContent taskList={taskList} content={content} mentionedPersonLookup={mentionedPersonLookup} />
    </div>
  );
}

function Subscriptions() {
  const { retrospective, isCurrentUserSubscribed } = useLoadedData();
  const refresh = useRefresh();

  const subscriptionsState = useCurrentSubscriptionsQueryAdapter({
    potentialSubscribers: retrospective.potentialSubscribers ?? [],
    subscriptionList: retrospective.subscriptionList,
    resourceName: "project retrospective",
    type: "project_retrospective",
    onRefresh: refresh,
  });

  if (!retrospective.potentialSubscribers || !retrospective.subscriptionList) {
    return null;
  }

  return (
    <>
      <div className="border-t border-stroke-base mt-16 mb-8" />

      <CurrentSubscriptions
        {...subscriptionsState}
        isCurrentUserSubscribed={isCurrentUserSubscribed}
        canEditSubscribers={retrospective.permissions?.canEdit || false}
      />
    </>
  );
}
